import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ImageUp, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, assetUrl, errMsg } from "@/lib/api";
import { shrinkImage } from "@/lib/images";
import { PHOTOS_QUERY, fetchPhotos } from "@/hooks/usePhotos";

export const CONTENT_QUERY = ["site-content"];
const EDIT_FLAG = "tt_edit";
const Ctx = createContext({ texts: {}, imgs: {}, editing: false });

const fetchContent = () => api.get("/content").then((r) => (r.data && typeof r.data === "object" ? r.data : {}));

export function ContentProvider({ children }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [flag, setFlag] = useState(() => sessionStorage.getItem(EDIT_FLAG) === "1");
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("edit") === "1") {
      sessionStorage.setItem(EDIT_FLAG, "1");
      setFlag(true);
    }
  }, []);
  const { data: texts } = useQuery({ queryKey: CONTENT_QUERY, queryFn: fetchContent, staleTime: 60_000 });
  const { data: imgs } = useQuery({ queryKey: PHOTOS_QUERY, queryFn: fetchPhotos, staleTime: 60_000 });

  const save = useCallback(async (key, value) => {
    try {
      await api.put("/admin/content", { key, value });
      qc.setQueryData(CONTENT_QUERY, (old) => ({ ...(old || {}), [key]: value }));
      toast.success("Saved");
    } catch (err) {
      toast.error(errMsg(err));
    }
  }, [qc]);

  const exit = useCallback(() => {
    sessionStorage.removeItem(EDIT_FLAG);
    setFlag(false);
  }, []);

  const value = { texts: texts ?? {}, imgs: imgs ?? {}, editing: flag && user?.role === "admin", save, exit };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useContent = () => useContext(Ctx);

export function useText(k, fallback) {
  const { texts } = useContent();
  return texts[k] ?? fallback;
}

/** Editable text. `children` is the default copy; admins can change it in edit mode. */
export function T({ k, children, as: Tag = "span", className }) {
  const { texts, editing, save } = useContent();
  const value = texts[k] ?? children;
  if (!editing) return className || Tag !== "span" ? <Tag className={className}>{value}</Tag> : <>{value}</>;
  return (
    <Tag key={value} data-cms={k} contentEditable suppressContentEditableWarning className={`${className || ""} tt-editable`}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); e.currentTarget.blur(); }
        else if (e.key === " ") { e.preventDefault(); document.execCommand("insertText", false, " "); }
      }}
      onKeyUp={(e) => { if (e.key === " ") e.preventDefault(); }}
      onBlur={(e) => {
        const next = e.currentTarget.innerText.trim();
        if (!next) e.currentTarget.innerText = value;
        else if (next !== value) save(k, next);
      }}>
      {value}
    </Tag>
  );
}

function ImgEditButton({ k, place = "top-3 right-3" }) {
  const qc = useQueryClient();
  const { imgs } = useContent();
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const key = `img:${k}`;
  const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };

  const upload = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";
    if (!picked) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("key", key);
      fd.append("file", await shrinkImage(picked));
      await api.post("/admin/photos", fd);
      await qc.invalidateQueries({ queryKey: PHOTOS_QUERY });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    await api.delete("/admin/photos", { params: { key } }).catch((err) => toast.error(errMsg(err)));
    await qc.invalidateQueries({ queryKey: PHOTOS_QUERY });
  };

  return (
    <span className={`absolute ${place} z-40 flex gap-1.5`} data-cms-img={k}>
      {imgs[key] && (
        <button type="button" onClick={stop(reset)} title="Original photo" data-testid={`cms-img-reset-${k}`}
          className="grid place-items-center h-9 w-9 rounded-full bg-paper text-ink shadow-lg"><RotateCcw className="h-4 w-4" /></button>
      )}
      <button type="button" onClick={stop(() => input.current?.click())} disabled={busy} data-testid={`cms-img-change-${k}`}
        className="inline-flex items-center gap-1.5 rounded-full bg-terra text-paper px-3.5 py-2 text-xs font-semibold shadow-lg disabled:opacity-60">
        <ImageUp className="h-4 w-4" /> {busy ? "Uploading…" : "Change photo"}
      </button>
      {createPortal(<input ref={input} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} />, document.body)}
    </span>
  );
}

export function useImg(k, fallback) {
  const { imgs } = useContent();
  return assetUrl(imgs[`img:${k}`]) || fallback;
}

/** Editable image. Renders `as` (img or motion.img) plus a "Change photo" control in edit mode. */
export function EImg({ k, src, as: Comp = "img", editable = true, place, ...rest }) {
  const { editing } = useContent();
  const url = useImg(k, src);
  return (
    <>
      <Comp src={url} {...rest} />
      {editing && editable && <ImgEditButton k={k} place={place} />}
    </>
  );
}
